/**
 * Applicant Form_Renderer — Field_Registry
 * (application-form-builder spec, task 23.1).
 *
 * Maps each supported `FieldType` (TEXT, LONG_TEXT, NUMBER, DATE, EMAIL, PHONE,
 * SELECT, CHECKBOX, FILE_UPLOAD — Req 3.1) to one of the app's own accessible
 * shadcn/ui controls. Every control is a *controlled* input that reads and
 * writes the typed `ResponseValue` discriminated union the rest of the
 * Application_Form stack speaks (`fieldValidation.ts#ResponseValue`), so the
 * value a control produces is exactly what `deriveZodSchema` /
 * `validateResponse` validate.
 *
 * Each control is intentionally presentational and uniform: it receives the
 * field definition, the current value, an `onChange`, accessibility ids, and an
 * `invalid` flag, and renders only the input itself. The label, optional help
 * text, and the error slot are owned by `FieldRenderer` (so they wrap every
 * control identically). The LONG_TEXT word/character counter affordance is added
 * by `FieldRenderer` (task 23.2). The FILE_UPLOAD control owns its own pick-time
 * constraint rejection messaging (content type / size / count, Req 5.6) because
 * rejected files are never uploaded and so never reach the response schema.
 * Bound controls (Division selector, Title preferences, Contact identity,
 * Document slot) are task 23.3 — this registry handles CUSTOM field types only.
 *
 * Value mapping (mirrors `validateValue` in `fieldValidation.ts`):
 *   - TEXT / EMAIL / PHONE / single SELECT → `{ kind: 'text' }`
 *   - LONG_TEXT                            → `{ kind: 'text' }` (multiline)
 *   - NUMBER                               → `{ kind: 'number' }`
 *   - DATE                                 → `{ kind: 'date' }` (ISO YYYY-MM-DD)
 *   - CHECKBOX with options                → `{ kind: 'list' }` (multi-select)
 *   - CHECKBOX without options             → `{ kind: 'boolean' }` (single)
 *   - FILE_UPLOAD                          → `{ kind: 'files' }`
 *
 * A cleared single-value control emits `undefined` (an unanswered field) rather
 * than an empty typed value, so an untouched optional EMAIL/PHONE/etc. is never
 * flagged for a format it does not have, while `validateResponse` still fires
 * REQUIRED on submit (its `isAnswered` treats a blank as unanswered).
 *
 * Validates: Requirements 3.1, 5.1
 */

/*
 * This is a registry/utility module, not a Fast-Refresh screen: it co-locates
 * the per-type control components with the registry map, the FILE_UPLOAD seam
 * context, and the `getFieldControl` resolver. Exporting those non-component
 * bindings alongside the controls is intentional, so the Fast-Refresh
 * "only export components" rule does not apply here (cf. the shadcn `ui`
 * primitives that disable it for their `*Variants` exports).
 */
/* eslint-disable react-refresh/only-export-components */

import * as React from 'react';

import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import {
  FieldType,
  validateValue,
  type FieldValidationError,
  type FileRef,
  type FormFieldDef,
  type ResponseValue,
} from './pure';

// ---------------------------------------------------------------------------
// Control contract
// ---------------------------------------------------------------------------

/** Props every Field_Registry control receives from `FieldRenderer`. */
export interface FieldControlProps {
  /** The CUSTOM field being rendered. */
  field: FormFieldDef;
  /** The current typed value, or `undefined` when unanswered. */
  value: ResponseValue | undefined;
  /** Commit a new value (or `undefined` to clear the field). */
  onChange: (value: ResponseValue | undefined) => void;
  /** Touch handler, forwarded to React Hook Form so blur-validation can run. */
  onBlur?: () => void;
  /** Whether the control is disabled (e.g. an approval-locked or read-only page). */
  disabled?: boolean;
  /** Whether the field currently has a validation error (drives `aria-invalid`). */
  invalid?: boolean;
  /** The control's DOM id — matches the `<label htmlFor>` rendered by `FieldRenderer`. */
  controlId: string;
  /** The field label element's id, for group controls' `aria-labelledby`. */
  labelId: string;
  /** Space-separated id list for `aria-describedby` (help + error slots). */
  describedBy?: string;
}

/** A Field_Registry control component. */
export type FieldControl = (props: FieldControlProps) => React.ReactElement;

// ---------------------------------------------------------------------------
// FILE_UPLOAD seam
// ---------------------------------------------------------------------------

/**
 * An injectable adapter that turns the browser `File`s an applicant picks into
 * the stored `FileRef[]` (e.g. by uploading to S3 and returning the keys). It is
 * provided through {@link FieldRegistryContext}. Wiring a concrete adapter (and
 * the draft/submit persistence around it) is a later task; when no adapter is
 * present the FILE_UPLOAD control renders its accessible affordance but does not
 * attempt to capture files.
 */
export interface FileUploadAdapter {
  upload: (field: FormFieldDef, files: FileList) => Promise<FileRef[]>;
}

/** Context carrying renderer-level seams (currently the FILE_UPLOAD adapter). */
export const FieldRegistryContext = React.createContext<{
  fileUploadAdapter?: FileUploadAdapter;
}>({});

// ---------------------------------------------------------------------------
// Value readers (narrow the discriminated union for the native control props)
// ---------------------------------------------------------------------------

function asText(value: ResponseValue | undefined): string {
  return value && value.kind === 'text' ? value.value : '';
}

function asDate(value: ResponseValue | undefined): string {
  return value && value.kind === 'date' ? value.value : '';
}

function asNumberInput(value: ResponseValue | undefined): string {
  return value && value.kind === 'number' && Number.isFinite(value.value)
    ? String(value.value)
    : '';
}

function asBoolean(value: ResponseValue | undefined): boolean {
  return value !== undefined && value.kind === 'boolean' ? value.value : false;
}

function asList(value: ResponseValue | undefined): string[] {
  return value !== undefined && value.kind === 'list' ? value.value : [];
}

function asFiles(value: ResponseValue | undefined): FileRef[] {
  return value !== undefined && value.kind === 'files' ? value.value : [];
}

/** Shared className for the native text-like inputs so they sit on the design grid. */
const TEXT_INPUT_CLASS = 'border-border-strong';

// ---------------------------------------------------------------------------
// Text-like controls (TEXT, LONG_TEXT, EMAIL, PHONE)
// ---------------------------------------------------------------------------

/** Build a single-line text-like control bound to the `text` ResponseValue kind. */
function makeTextInputControl(inputType: 'text' | 'email' | 'tel'): FieldControl {
  return function TextInputControl({
    field,
    value,
    onChange,
    onBlur,
    disabled,
    invalid,
    controlId,
    describedBy,
  }: FieldControlProps): React.ReactElement {
    return (
      <Input
        id={controlId}
        type={inputType}
        value={asText(value)}
        disabled={disabled}
        aria-required={field.required || undefined}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        className={TEXT_INPUT_CLASS}
        onBlur={onBlur}
        onChange={(e) => {
          const next = e.target.value;
          onChange(next === '' ? undefined : { kind: 'text', value: next });
        }}
      />
    );
  };
}

function LongTextControl({
  field,
  value,
  onChange,
  onBlur,
  disabled,
  invalid,
  controlId,
  describedBy,
}: FieldControlProps): React.ReactElement {
  return (
    <Textarea
      id={controlId}
      value={asText(value)}
      disabled={disabled}
      rows={4}
      aria-required={field.required || undefined}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      className={cn(TEXT_INPUT_CLASS, 'min-h-[120px] leading-relaxed')}
      onBlur={onBlur}
      onChange={(e) => {
        const next = e.target.value;
        onChange(next === '' ? undefined : { kind: 'text', value: next });
      }}
    />
  );
}

function NumberControl({
  field,
  value,
  onChange,
  onBlur,
  disabled,
  invalid,
  controlId,
  describedBy,
}: FieldControlProps): React.ReactElement {
  return (
    <Input
      id={controlId}
      type="number"
      inputMode="numeric"
      value={asNumberInput(value)}
      disabled={disabled}
      aria-required={field.required || undefined}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      className={TEXT_INPUT_CLASS}
      onBlur={onBlur}
      onChange={(e) => {
        const raw = e.target.value;
        onChange(raw === '' ? undefined : { kind: 'number', value: Number(raw) });
      }}
    />
  );
}

function DateControl({
  field,
  value,
  onChange,
  onBlur,
  disabled,
  invalid,
  controlId,
  describedBy,
}: FieldControlProps): React.ReactElement {
  return (
    <Input
      id={controlId}
      type="date"
      value={asDate(value)}
      disabled={disabled}
      aria-required={field.required || undefined}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      className={TEXT_INPUT_CLASS}
      onBlur={onBlur}
      onChange={(e) => {
        const raw = e.target.value;
        onChange(raw === '' ? undefined : { kind: 'date', value: raw });
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// SELECT — single-select dropdown (`text` kind)
// ---------------------------------------------------------------------------

function SelectControl({
  field,
  value,
  onChange,
  disabled,
  invalid,
  controlId,
  describedBy,
}: FieldControlProps): React.ReactElement {
  const current = asText(value);
  return (
    <Select
      value={current === '' ? undefined : current}
      disabled={disabled}
      onValueChange={(next) => onChange(next === '' ? undefined : { kind: 'text', value: next })}
    >
      <SelectTrigger
        id={controlId}
        aria-required={field.required || undefined}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        className={cn(TEXT_INPUT_CLASS, 'w-full')}
      >
        <SelectValue placeholder="Select an option" />
      </SelectTrigger>
      <SelectContent>
        {(field.options ?? []).map((option) => (
          <SelectItem key={option} value={option}>
            {option}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ---------------------------------------------------------------------------
// CHECKBOX — single boolean (no options) or multi-select group (with options)
// ---------------------------------------------------------------------------

function CheckboxControl(props: FieldControlProps): React.ReactElement {
  const hasOptions = (props.field.options ?? []).length > 0;
  return hasOptions ? <CheckboxGroupControl {...props} /> : <SingleCheckboxControl {...props} />;
}

function SingleCheckboxControl({
  field,
  value,
  onChange,
  disabled,
  invalid,
  controlId,
  describedBy,
}: FieldControlProps): React.ReactElement {
  return (
    <Checkbox
      id={controlId}
      checked={asBoolean(value)}
      disabled={disabled}
      aria-required={field.required || undefined}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      onCheckedChange={(checked) => onChange({ kind: 'boolean', value: checked })}
    />
  );
}

function CheckboxGroupControl({
  field,
  value,
  onChange,
  disabled,
  invalid,
  labelId,
  describedBy,
}: FieldControlProps): React.ReactElement {
  const selected = asList(value);
  const toggle = (option: string) => {
    const next = selected.includes(option)
      ? selected.filter((o) => o !== option)
      : [...selected, option];
    onChange(next.length === 0 ? undefined : { kind: 'list', value: next });
  };
  return (
    <div
      role="group"
      aria-labelledby={labelId}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      className="grid gap-2 rounded-input border border-border-strong p-3 sm:grid-cols-2"
    >
      {(field.options ?? []).map((option) => {
        const checked = selected.includes(option);
        return (
          <label
            key={option}
            className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-muted"
          >
            <Checkbox
              checked={checked}
              disabled={disabled}
              onCheckedChange={() => toggle(option)}
              aria-label={option}
            />
            <span className="text-body text-foreground">{option}</span>
          </label>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FILE_UPLOAD — accessible uploader (capture wired via FieldRegistryContext)
// ---------------------------------------------------------------------------

function FileUploadControl({
  field,
  value,
  onChange,
  disabled,
  invalid,
  controlId,
  describedBy,
}: FieldControlProps): React.ReactElement {
  const { fileUploadAdapter } = React.useContext(FieldRegistryContext);
  const files = asFiles(value);
  const [busy, setBusy] = React.useState(false);
  // Client-side constraint rejections for the just-picked files (Req 5.6). These
  // are distinct from the React Hook Form schema error on the stored value:
  // rejected files are never uploaded, so they never enter the response and the
  // schema never sees them — the renderer must surface the rejection itself.
  const [rejections, setRejections] = React.useState<string[]>([]);

  const accept = field.allowedContentTypes?.length
    ? field.allowedContentTypes.join(',')
    : undefined;
  const allowMultiple = field.maxFileCount !== 1;
  const rejectionId = `${controlId}-file-rejection`;

  const handlePick = async (picked: FileList | null) => {
    if (!picked || picked.length === 0 || !fileUploadAdapter) {
      return;
    }

    // Validate the picked browser files against the field's FILE_UPLOAD
    // constraints (content type / size / count) BEFORE uploading, reusing the
    // exact `validateValue` rules the server runs (Req 5.6). A browser `File`
    // exposes everything those rules read (name, type, size); the not-yet-known
    // `s3Key` is irrelevant to the constraint checks, so a placeholder is safe.
    const candidates: FileRef[] = Array.from(picked).map((file) => ({
      s3Key: '',
      fileName: file.name,
      contentType: file.type,
      sizeBytes: file.size,
    }));
    const constraintErrors: FieldValidationError[] = validateValue(field, {
      kind: 'files',
      value: candidates,
    });
    if (constraintErrors.length > 0) {
      // Reject: surface the constraint-naming messages and persist nothing.
      setRejections(constraintErrors.map((e) => e.message));
      return;
    }

    setRejections([]);
    setBusy(true);
    try {
      const refs = await fileUploadAdapter.upload(field, picked);
      onChange(refs.length === 0 ? undefined : { kind: 'files', value: refs });
    } finally {
      setBusy(false);
    }
  };

  const describedByWithRejection =
    rejections.length > 0 ? [describedBy, rejectionId].filter(Boolean).join(' ') : describedBy;

  return (
    <div className="space-y-2">
      <Input
        id={controlId}
        type="file"
        accept={accept}
        multiple={allowMultiple}
        disabled={disabled || busy || !fileUploadAdapter}
        aria-required={field.required || undefined}
        aria-invalid={invalid || rejections.length > 0 || undefined}
        aria-describedby={describedByWithRejection}
        className={cn(TEXT_INPUT_CLASS, 'cursor-pointer file:mr-3 file:cursor-pointer')}
        onChange={(e) => void handlePick(e.target.files)}
      />
      {field.allowedContentTypes?.length || field.maxFileSizeBytes || field.maxFileCount ? (
        <p className="text-body-sm text-muted-foreground">{describeFileConstraints(field)}</p>
      ) : null}
      {rejections.length > 0 ? (
        <ul id={rejectionId} role="alert" className="space-y-1 text-body-sm text-destructive-text">
          {rejections.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      ) : null}
      {files.length > 0 ? (
        <ul className="space-y-1 text-body-sm text-muted-foreground">
          {files.map((file) => (
            <li key={file.s3Key}>{file.fileName}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** A short, human-readable summary of a FILE_UPLOAD field's constraints. */
function describeFileConstraints(field: FormFieldDef): string {
  const parts: string[] = [];
  if (field.allowedContentTypes?.length) {
    parts.push(`Accepted types: ${field.allowedContentTypes.join(', ')}`);
  }
  if (typeof field.maxFileSizeBytes === 'number' && field.maxFileSizeBytes > 0) {
    parts.push(`Max size: ${formatBytes(field.maxFileSizeBytes)}`);
  }
  if (typeof field.maxFileCount === 'number' && field.maxFileCount > 0) {
    parts.push(`Max files: ${field.maxFileCount}`);
  }
  return parts.join(' · ');
}

/** Format a byte count into a compact, human-readable size (display only). */
function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  const kb = bytes / 1024;
  if (kb < 1024) {
    return `${Math.round(kb)} KB`;
  }
  const mb = kb / 1024;
  return `${Math.round(mb * 10) / 10} MB`;
}

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

/** The Field_Registry: each supported `FieldType` mapped to its control (Req 3.1). */
export const FIELD_CONTROL_REGISTRY: Record<FieldType, FieldControl> = {
  [FieldType.TEXT]: makeTextInputControl('text'),
  [FieldType.LONG_TEXT]: LongTextControl,
  [FieldType.NUMBER]: NumberControl,
  [FieldType.DATE]: DateControl,
  [FieldType.EMAIL]: makeTextInputControl('email'),
  [FieldType.PHONE]: makeTextInputControl('tel'),
  [FieldType.SELECT]: SelectControl,
  [FieldType.CHECKBOX]: CheckboxControl,
  [FieldType.FILE_UPLOAD]: FileUploadControl,
};

/**
 * Resolve the control for a CUSTOM field's `fieldType`. Returns `null` for an
 * absent/unknown type so the caller can render a safe fallback rather than
 * throw (definition-time validation already rejects unsupported types).
 */
export function getFieldControl(fieldType: FormFieldDef['fieldType']): FieldControl | null {
  if (fieldType === undefined) {
    return null;
  }
  return FIELD_CONTROL_REGISTRY[fieldType] ?? null;
}
